import { OrganizationApplicationParameters, RevenueAmount } from '@platform/client/schemas';
import { FormsInputDropdown, RadioGroup } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { GradientShowcase } from '../../GradientShowcase';
import image from '../OrgImages/marietestimonial.svg';

type FormValues = {
  revenueGeneration: string;
  revenueAmount?: RevenueAmount;
};

const ORG_REVENUE_OPTIONS = [
  { text: '$1,000 - $10,000', textValue: RevenueAmount['1,000_-_10,000'] },
  { text: '$10,000 - 50,000', textValue: RevenueAmount['10,000_-_50,000'] },
  { text: '$50,000 - 100,000', textValue: RevenueAmount['50,000_-_100,000'] },
  { text: '$100,000 - 250,000', textValue: RevenueAmount['100,000_-_250,000'] },
  { text: '$250,000 - 500,000', textValue: RevenueAmount['250,000_-_500,000'] },
  { text: '$500,000 - 1,000,000', textValue: RevenueAmount['500,000_-_1,000,000'] },
  { text: '$1,000,000 - 2,500,000', textValue: RevenueAmount['1,000,000_-_2,500,000'] },
  { text: '$2,500,000+', textValue: RevenueAmount['2,500,000+'] },
];

export const OrgRevenueGenerationSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      revenueGeneration: getApplicationDataByKey('revenueGeneration') || '',
      revenueAmount: getApplicationDataByKey('revenueAmount') || '',
    },
  });

  const onChange = (value?: string | number) => {
    setValue('revenueGeneration', value as string);
  };

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      revenueGeneration: data.revenueGeneration,
      revenueAmount: data.revenueAmount || null,
    });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      revenueGeneration: watch('revenueGeneration'),
      revenueAmount: watch('revenueAmount'),
    });
    onBack();
  };
  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      customLeftPanelContent={<GradientShowcase image={image} />}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title="Let's talk about how your organization operates"
        description={
          "We'll start with revenue to better understand your stage of growth so that we can tailor support to your needs."
        }
      >
        <RadioGroup
          name='org-revenue-generation-selection'
          aria-label='org-revenue-generation-selection'
          register={() => {
            return register('revenueGeneration', {
              required: "Let us know if you're generating revenue to continue",
            });
          }}
          error={errors.revenueGeneration?.message}
          items={[
            {
              title: 'Our product or services is currently generating revenue',
              value: 'revenue-generating',
            },
            {
              value: 'non-revenue-generating',
              title: 'We are not currently generating revenue from our product or service',
            },
          ]}
          defaultValue={getApplicationDataByKey('revenueGeneration')}
          onChange={onChange}
        />
        {watch('revenueGeneration') === 'revenue-generating' && (
          <FormsInputDropdown
            control={control}
            name='revenueAmount'
            rules={{ required: 'Select your revenues in the last 12 months to continue' }}
            label='What were your total revenues in the last 12 months (in USD)?'
            placeholder='Select one'
            items={ORG_REVENUE_OPTIONS}
            type='default'
            error={errors.revenueAmount}
          />
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
